import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Modal, Table } from '@heroui/react';
import type { LocaleCode } from '@/api/models';
import { DatePickerField } from '@/components/datePickerField';
import { formatGel } from '@/pages/accounting/books/chartOfAccounts/utils';
import { JournalEntryModal } from '@/pages/accounting/journalEntry';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { formatPeriod } from '../../fixedAssetsMeta';
import { useDepreciationRun } from './useDepreciationRun';

type DepreciationRunDialogProps = {
    isOpen: boolean;
    locale: LocaleCode;
    onClose: () => void;
};

/**
 * Run depreciation for a month.
 *
 * Shows every active asset's computed charge for the period and the total, then
 * builds **one** balanced draft entry — the period's depreciation grouped by
 * account, not one entry per category — and hands it to the Journal Entry form.
 * Nothing posts automatically.
 *
 * Assets that contribute nothing appear as an en dash with the reason spelled
 * out beside them (fully depreciated, already charged, not yet in service, life
 * ended), because a zero the user cannot explain is indistinguishable from a bug.
 *
 * A **catch-up** is the inverse case and needs the same treatment: it charges
 * several months at once because earlier periods were never run, so it is
 * labelled with its instalment count. A ₾3,000 month with no explanation reads
 * as a bug; "catching up 3 months" reads as the correct run it is.
 *
 * A period that has already been run is surfaced, not silently re-run: the
 * existing entry is offered instead.
 */
export const DepreciationRunDialog: React.FC<DepreciationRunDialogProps> = ({
    isOpen,
    locale,
    onClose,
}) => {
    const { t } = useTranslation('pages');
    const model = useDepreciationRun({ locale, onClose });
    const alreadyRun = model.existingRun !== null;

    return (
        <>
            <Modal
                isOpen={isOpen && model.draft === null}
                onOpenChange={(open) => !open && onClose()}
            >
                <Modal.Backdrop>
                    <Modal.Container size="lg">
                        <Modal.Dialog
                            className="w-full max-w-2xl"
                            {...getNodeTestIdProp('fixed-assets-run-dialog')}
                        >
                            <Modal.CloseTrigger />
                            <Modal.Header>
                                <Modal.Heading>
                                    {t('fixedAssets.run.title')}
                                </Modal.Heading>
                                <p className="text-sm text-muted">
                                    {t('fixedAssets.run.subtitle')}
                                </p>
                            </Modal.Header>

                            <Modal.Body className="flex flex-col gap-4 pb-2">
                                <div className="flex flex-wrap items-end gap-4">
                                    <div className="w-full max-w-[15rem]">
                                        <DatePickerField
                                            id="fixed-assets-run-period"
                                            label={t(
                                                'fixedAssets.run.periodLabel',
                                            )}
                                            name="period"
                                            onChange={model.onPeriodDateChange}
                                            value={model.periodDate}
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1 pb-1">
                                        <span className="text-xs font-medium uppercase tracking-wide text-muted">
                                            {t('fixedAssets.run.periodCovered')}
                                        </span>
                                        <span
                                            className="text-sm font-semibold text-foreground"
                                            {...getNodeTestIdProp(
                                                'fixed-assets-run-period-label',
                                            )}
                                        >
                                            {formatPeriod(model.period, t)}
                                        </span>
                                    </div>
                                    <Button
                                        isPending={model.isPreviewing}
                                        onPress={model.onPreview}
                                        variant="outline"
                                        {...getNodeTestIdProp(
                                            'fixed-assets-run-preview',
                                        )}
                                    >
                                        {t('fixedAssets.run.preview')}
                                    </Button>
                                </div>

                                <p className="text-xs text-muted">
                                    {t('fixedAssets.run.prorationNote')}
                                </p>

                                {alreadyRun ? (
                                    <div
                                        className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-warning-soft px-3 py-2"
                                        {...getNodeTestIdProp(
                                            'fixed-assets-run-existing',
                                        )}
                                    >
                                        <span className="flex items-center gap-2 text-sm text-warning-soft-foreground">
                                            <Icon
                                                aria-hidden
                                                icon="gravity-ui:circle-info"
                                            />
                                            {t('fixedAssets.run.alreadyRun', {
                                                period: formatPeriod(
                                                    model.period,
                                                    t,
                                                ),
                                            })}
                                        </span>
                                        <Button
                                            isDisabled={
                                                model.existingRun
                                                    ?.journalEntryId === null
                                            }
                                            onPress={model.onOpenExistingEntry}
                                            size="sm"
                                            variant="outline"
                                            {...getNodeTestIdProp(
                                                'fixed-assets-run-open-existing',
                                            )}
                                        >
                                            {t(
                                                'fixedAssets.run.openExistingEntry',
                                            )}
                                        </Button>
                                    </div>
                                ) : null}

                                {model.previewError ? (
                                    <p
                                        className="text-sm text-danger"
                                        {...getNodeTestIdProp(
                                            'fixed-assets-run-error',
                                        )}
                                    >
                                        {model.previewError}
                                    </p>
                                ) : null}

                                {model.hasPreview ? (
                                    <div
                                        {...getNodeTestIdProp(
                                            'fixed-assets-run-table',
                                        )}
                                    >
                                        <Table>
                                            <Table.ScrollContainer className="max-h-72 overflow-y-auto">
                                                <Table.Content
                                                    aria-label={t(
                                                        'fixedAssets.run.tableAria',
                                                    )}
                                                    className="text-sm"
                                                >
                                                    <Table.Header>
                                                        <Table.Column
                                                            id="name"
                                                            isRowHeader
                                                        >
                                                            {t(
                                                                'fixedAssets.columns.name',
                                                            )}
                                                        </Table.Column>
                                                        <Table.Column id="charge">
                                                            {t(
                                                                'fixedAssets.run.charge',
                                                            )}
                                                        </Table.Column>
                                                    </Table.Header>
                                                    <Table.Body>
                                                        {model.rows.map(
                                                            (row) => (
                                                                <Table.Row
                                                                    id={
                                                                        row.assetId
                                                                    }
                                                                    key={
                                                                        row.assetId
                                                                    }
                                                                    {...getNodeTestIdProp(
                                                                        `fixed-assets-run-row-${row.assetId}`,
                                                                    )}
                                                                >
                                                                    <Table.Cell>
                                                                        <span className="flex flex-col">
                                                                            <span className="text-foreground">
                                                                                {
                                                                                    row.name
                                                                                }
                                                                            </span>
                                                                            {row.reasonKey ? (
                                                                                <span
                                                                                    className={cn(
                                                                                        'text-xs',
                                                                                        row.isCatchUp
                                                                                            ? 'text-warning'
                                                                                            : 'text-muted',
                                                                                    )}
                                                                                    {...getNodeTestIdProp(
                                                                                        `fixed-assets-run-reason-${row.assetId}`,
                                                                                    )}
                                                                                >
                                                                                    {t(
                                                                                        row.reasonKey,
                                                                                        row.reasonValues,
                                                                                    )}
                                                                                </span>
                                                                            ) : null}
                                                                        </span>
                                                                    </Table.Cell>
                                                                    <Table.Cell>
                                                                        <span
                                                                            className={cn(
                                                                                'block text-right tabular-nums',
                                                                                row.isZero
                                                                                    ? 'text-muted'
                                                                                    : 'text-foreground',
                                                                            )}
                                                                        >
                                                                            {
                                                                                row.amountText
                                                                            }
                                                                        </span>
                                                                    </Table.Cell>
                                                                </Table.Row>
                                                            ),
                                                        )}
                                                        {/* The total is the last
                                                            body row, not a
                                                            `Table.Footer`: the
                                                            footer slot renders
                                                            outside the table, and
                                                            the total must stay
                                                            column-aligned with the
                                                            charges above it. */}
                                                        <Table.Row
                                                            className="border-t-2 border-foreground/50 bg-surface-secondary"
                                                            id="fixed-assets-run-total-row"
                                                            key="fixed-assets-run-total-row"
                                                        >
                                                            <Table.Cell>
                                                                <span className="text-sm font-semibold">
                                                                    {t(
                                                                        'fixedAssets.run.total',
                                                                    )}
                                                                </span>
                                                            </Table.Cell>
                                                            <Table.Cell
                                                                {...getNodeTestIdProp(
                                                                    'fixed-assets-run-total',
                                                                )}
                                                            >
                                                                <span className="block text-right text-sm font-bold tabular-nums text-foreground">
                                                                    {formatGel(
                                                                        model.totalTetri,
                                                                    )}
                                                                </span>
                                                            </Table.Cell>
                                                        </Table.Row>
                                                    </Table.Body>
                                                </Table.Content>
                                            </Table.ScrollContainer>
                                        </Table>
                                    </div>
                                ) : null}

                                {model.isNilRun ? (
                                    <p
                                        className="flex items-center gap-2 text-sm text-muted"
                                        {...getNodeTestIdProp(
                                            'fixed-assets-run-nil',
                                        )}
                                    >
                                        <Icon
                                            aria-hidden
                                            icon="gravity-ui:circle-info"
                                        />
                                        {t('fixedAssets.run.nothingToCharge')}
                                    </p>
                                ) : null}
                            </Modal.Body>

                            <Modal.Footer>
                                <Button
                                    onPress={onClose}
                                    variant="tertiary"
                                    {...getNodeTestIdProp(
                                        'fixed-assets-run-cancel',
                                    )}
                                >
                                    {t('fixedAssets.actions.cancel')}
                                </Button>
                                <Button
                                    isDisabled={
                                        !model.hasPreview ||
                                        model.isNilRun ||
                                        alreadyRun
                                    }
                                    isPending={model.isCommitting}
                                    onPress={model.onReview}
                                    variant="primary"
                                    {...getNodeTestIdProp(
                                        'fixed-assets-run-review',
                                    )}
                                >
                                    {t('fixedAssets.actions.reviewEntry')}
                                </Button>
                            </Modal.Footer>
                        </Modal.Dialog>
                    </Modal.Container>
                </Modal.Backdrop>
            </Modal>

            <JournalEntryModal
                entry={model.draft}
                isOpen={model.draft !== null}
                onClose={model.onDraftClose}
                onSaved={model.onDraftSaved}
            />
        </>
    );
};
